import React, { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  List,
  Menu,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArchive,
  IconCheck,
  IconChevronDown,
  IconTag,
  IconTags,
  IconUser,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react";
import { clientStatuses } from "@/constants.ts";
import { useBulkUpdate } from "@utils/hooks/useBulkUpdate";
import { archiveClient, assignClientsToMembers } from "@utils/api";
import { getMembers } from "@utils/api/members";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import type { Member } from "@components/Tax Pro Only/Assignee/Assignee";
import { getConsistentColorForUser, getInitials } from "./utils/assigneeUtils";
import { useStatusStore } from "@utils/stores/useStatusStore";
import { useAuthStore } from "@utils/stores/authStore";
import ManageClientTagsModal from "@pages/TaxProClientList/components/ManageClientTagsModal/ManageClientTagsModal";
import "./BulkActionsToolbar.scss";

interface BulkActionsToolbarProps {
  selectedCount: number;
  selectedClientIds: string[];
  onClearSelection: () => void;
  onUpdateComplete: () => void;
  clients?: any[]; // Array of client objects to get names for archive confirmation
}

/**
 * BulkActionsToolbar component for performing bulk actions on selected clients
 * Allows users to change status or assignee for multiple clients at once
 */
const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  selectedClientIds,
  onClearSelection,
  onUpdateComplete,
  clients = [],
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const [
    archiveModalOpened,
    { open: openArchiveModal, close: closeArchiveModal },
  ] = useDisclosure(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const [tagsModalOpened, { open: openTagsModal, close: closeTagsModal }] =
    useDisclosure(false);

  const { bulkUpdateStatus, isUpdatingStatus } = useBulkUpdate();
  const queryClient = useQueryClient();
  const { selectedTaxYear } = useStatusStore();
  const { role } = useAuthStore();

  // Fetch members for assignee dropdown
  interface MembersResponse {
    businessStaff: Member[];
    counts: any;
    message: string;
  }

  const { data: membersData, isLoading: isLoadingMembers } = useQuery<
    MembersResponse,
    Error
  >({
    queryKey: ["members"],
    queryFn: async () => {
      const response = await getMembers();
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    enabled: role !== undefined && role !== "client", // Only fetch for tax pros
  });

  // Get active members for assignee dropdown
  const activeMembers = React.useMemo(() => {
    if (!membersData?.businessStaff) return [];
    return membersData.businessStaff
      .filter((member: any) => member?.status === "active")
      .map((member: any) => ({
        ...member,
        name: `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim(),
      }));
  }, [membersData]);

  // Get selected client names and archive status
  const { selectedClientNames, archiveStatus } = useMemo(() => {
    const names: string[] = [];
    let archivedCount = 0;
    let activeCount = 0;

    selectedClientIds.forEach((id) => {
      const client = clients.find((c) => (c.clientId || c.userId) === id);
      if (client) {
        names.push(
          `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
            "Unnamed Client"
        );

        // Check if client is archived
        // Backend returns isArchived in camelCase, but status field may still show non-archived status
        const isArchived = client.isArchived === true;
        if (isArchived) {
          archivedCount++;
        } else {
          activeCount++;
        }
      }
    });

    // Determine archive status: 'all-archived', 'all-active', or 'mixed'
    let status: "all-archived" | "all-active" | "mixed";
    if (archivedCount === selectedClientIds.length) {
      status = "all-archived";
    } else if (activeCount === selectedClientIds.length) {
      status = "all-active";
    } else {
      status = "mixed";
    }

    return {
      selectedClientNames: names,
      archiveStatus: status,
    };
  }, [selectedClientIds, clients]);

  const handleStatusClick = useCallback((status: string) => {
    handleBulkStatusUpdate(status);
  }, []);

  const handleBulkStatusUpdate = useCallback(
    async (status: string) => {
      // Log the actual selectedClientIds to debug
      console.log("Bulk update called with clientIds:", selectedClientIds);
      console.log("Selected count:", selectedCount);
      console.log("Selected tax year:", selectedTaxYear);

      if (!selectedClientIds || selectedClientIds.length === 0) {
        notifications.show({
          title: "Error",
          message: "No clients selected. Please select clients first.",
          color: "red",
        });
        return;
      }

      // Validate that tax year is selected
      if (!selectedTaxYear) {
        notifications.show({
          title: "Tax Year Required",
          message:
            "Please select a tax year filter before updating status. This ensures the correct year's status is updated for each client.",
          color: "red",
          icon: <IconAlertTriangle size={18} />,
          autoClose: 7000,
        });
        return;
      }

      try {
        const result = await bulkUpdateStatus(
          selectedClientIds,
          status,
          selectedTaxYear
        );

        // Only show success notification if all succeeded
        // Partial success is handled in the hook
        if (result.failed === 0) {
          notifications.show({
            title: "Success",
            message: `Status updated to "${status}" for ${result.success} client(s) in tax year ${selectedTaxYear}`,
            color: "green",
            icon: <IconCheck size={18} />,
          });
        }

        setSelectedStatus(null);
        onUpdateComplete();
      } catch (error) {
        console.error("Bulk status update error:", error);
        notifications.show({
          title: "Error",
          message: "Failed to update status. Please try again.",
          color: "red",
        });
      }
    },
    [
      selectedClientIds,
      selectedCount,
      selectedTaxYear,
      bulkUpdateStatus,
      onUpdateComplete,
    ]
  );

  const handleArchiveClick = useCallback(() => {
    if (archiveStatus !== "mixed") {
      openArchiveModal();
    }
  }, [archiveStatus, openArchiveModal]);

  const confirmArchive = useCallback(
    async (shouldArchive: boolean) => {
      setIsArchiving(true);
      const action = shouldArchive ? "archive" : "unarchive";
      const actionPastTense = shouldArchive ? "Archived" : "Unarchived";

      try {
        console.log(
          `Starting bulk ${action} for ${selectedClientIds.length} clients`
        );

        // Archive/unarchive each client using the correct endpoint
        const archivePromises = selectedClientIds.map(async (clientId) => {
          try {
            await archiveClient(clientId, shouldArchive);
            console.log(`Successfully ${action}d client ${clientId}`);
            return { success: true, clientId };
          } catch (error) {
            console.error(`Failed to ${action} client ${clientId}:`, error);
            return { success: false, clientId, error };
          }
        });

        const results = await Promise.allSettled(archivePromises);

        let successCount = 0;
        let failedCount = 0;

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            if (result.value.success) {
              successCount++;
            } else {
              failedCount++;
            }
          } else {
            failedCount++;
            console.error(
              `Promise rejected for client ${selectedClientIds[index]}:`,
              result.reason
            );
          }
        });

        console.log(
          `Bulk ${action} completed: ${successCount} succeeded, ${failedCount} failed out of ${selectedClientIds.length} total`
        );

        // Invalidate queries to refresh the client list
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === "clients" || key === "search" || key === "analytics";
          },
        });

        // Show success notification
        if (failedCount === 0) {
          notifications.show({
            title: "Success",
            message: `${actionPastTense} ${successCount} client(s)`,
            color: "green",
            icon: <IconCheck size={18} />,
          });
        } else {
          notifications.show({
            title: "Partial Success",
            message: `${actionPastTense} ${successCount} client(s). ${failedCount} failed.`,
            color: "yellow",
          });
        }

        closeArchiveModal();
        onUpdateComplete();
      } catch (error) {
        console.error("Bulk archive error:", error);
        notifications.show({
          title: "Error",
          message: "Failed to archive clients. Please try again.",
          color: "red",
        });
        closeArchiveModal();
      } finally {
        setIsArchiving(false);
      }
    },
    [selectedClientIds, queryClient, onUpdateComplete, closeArchiveModal]
  );

  const handleBulkAssigneeUpdate = useCallback(
    async (memberId: string | null) => {
      try {
        // Update each client's assignee using the assignment API
        const updatePromises = selectedClientIds.map((clientId) =>
          assignClientsToMembers({
            clientId,
            memberId,
          })
        );

        await Promise.all(updatePromises);

        // Batch invalidate all queries at once to trigger single refetch
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              key === "clients" ||
              key === "search" ||
              key === "members" ||
              key === "client" ||
              key === "analytics"
            );
          },
        });

        notifications.show({
          title: "Success",
          message: `Assignee updated for ${selectedCount} client(s)`,
          color: "green",
        });
        onUpdateComplete();
      } catch (error) {
        notifications.show({
          title: "Error",
          message: "Failed to update assignee. Please try again.",
          color: "red",
        });
      }
    },
    [selectedClientIds, selectedCount, onUpdateComplete, queryClient]
  );

  // Group statuses into categories for better organization
  const activeStatuses = Object.values(clientStatuses).filter(
    (item) => !["Archived", "Completed"].includes(item.status)
  );
  const finalStatuses = Object.values(clientStatuses).filter((item) =>
    ["Completed"].includes(item.status)
  );

  // Create status menu items with better visual design
  const renderStatusItem = (
    item: (typeof clientStatuses)[keyof typeof clientStatuses]
  ) => (
    <Menu.Item
      key={item.status}
      onClick={() => {
        setSelectedStatus(item.status);
        handleStatusClick(item.status);
      }}
      className="bulk-status-menu-item"
      disabled={isUpdatingStatus}
    >
      <div className="status-option-content">
        <Badge
          className={`status-badge statusClass-${item.class}`}
          variant="light"
          size="lg"
        >
          {item.status}
        </Badge>
        {isUpdatingStatus && selectedStatus === item.status && (
          <Text size="xs" c="dimmed" ml="auto">
            Updating...
          </Text>
        )}
      </div>
    </Menu.Item>
  );

  const activeStatusItems = activeStatuses.map(renderStatusItem);
  const finalStatusItems = finalStatuses.map(renderStatusItem);

  // Create assignee menu items with avatars - not memoized since they contain callbacks
  const assigneeMenuItems = [
    <Menu.Item
      key="unassigned"
      onClick={() => {
        handleBulkAssigneeUpdate(null);
      }}
    >
      <div className="assignee-option-content">
        <Avatar
          size={24}
          radius="xl"
          color="#6c757d"
          styles={{
            root: {
              backgroundColor: "#6c757d",
              color: "white",
            },
            placeholder: {
              color: "white",
            },
          }}
        >
          <IconUser size={14} />
        </Avatar>
        <Text size="sm">Unassigned</Text>
      </div>
    </Menu.Item>,
    ...activeMembers.map((member: any) => {
      const initials = getInitials(member.firstName, member.lastName);
      const avatarColor = getConsistentColorForUser(member.userId);
      return (
        <Menu.Item
          key={member.userId}
          onClick={() => {
            handleBulkAssigneeUpdate(member.userId);
          }}
        >
          <div className="assignee-option-content">
            <Avatar
              size={24}
              radius="xl"
              color={avatarColor}
              styles={{
                root: {
                  backgroundColor: avatarColor,
                  color: "white",
                },
                placeholder: {
                  color: "white",
                },
              }}
            >
              <span style={{ fontSize: "11px", fontWeight: 600 }}>
                {initials}
              </span>
            </Avatar>
            <Text size="sm">{member.name}</Text>
          </div>
        </Menu.Item>
      );
    }),
  ];

  return (
    <div className="bulk-actions-toolbar">
      <Group gap="md" align="center" justify="space-between" wrap="nowrap">
        <Group gap="sm" align="center">
          <div>
            <Text size="lg" fw={700} c="#1a1a1a">
              Bulk Operations
            </Text>
            <Text size="sm" c="#6c757d">
              {selectedCount} client{selectedCount !== 1 ? "s" : ""} selected
            </Text>
          </div>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onClearSelection}
            aria-label="Clear selection"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>

        <Group gap="sm" align="center">
          {/* Bulk Archive/Unarchive Button */}
          <Tooltip
            label="Cannot archive/unarchive mixed selection. Please select only archived or only active clients."
            disabled={archiveStatus !== "mixed"}
            withArrow
          >
            <Button
              leftSection={<IconArchive size={16} />}
              variant="default"
              size="md"
              onClick={handleArchiveClick}
              disabled={
                isUpdatingStatus || isArchiving || archiveStatus === "mixed"
              }
              loading={isArchiving}
              className="bulk-archive-button"
            >
              {archiveStatus === "all-archived" ? "Unarchive" : "Archive"}
            </Button>
          </Tooltip>

          {/* Bulk Status Update */}
          <Menu shadow="md" width={280} position="bottom-start">
            <Menu.Target>
              <Button
                leftSection={<IconTag size={16} />}
                rightSection={<IconChevronDown size={16} />}
                variant="default"
                size="md"
                loading={isUpdatingStatus}
                disabled={isUpdatingStatus}
                className="bulk-status-button"
              >
                Change Status
              </Button>
            </Menu.Target>
            <Menu.Dropdown className="bulk-status-dropdown">
              <Menu.Label className="bulk-status-label">
                Active Statuses
              </Menu.Label>
              <ScrollArea.Autosize
                type="always"
                scrollbarSize={8}
                offsetScrollbars
                mah={250}
              >
                {activeStatusItems}
              </ScrollArea.Autosize>
              {finalStatusItems.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Menu.Label className="bulk-status-label">
                    Final Statuses
                  </Menu.Label>
                  <ScrollArea.Autosize
                    type="always"
                    scrollbarSize={8}
                    offsetScrollbars
                    mah={150}
                  >
                    {finalStatusItems}
                  </ScrollArea.Autosize>
                </>
              )}
            </Menu.Dropdown>
          </Menu>

          {/* Bulk Manage Tags */}
          <Button
            leftSection={<IconTags size={16} />}
            variant="default"
            size="md"
            onClick={openTagsModal}
            className="bulk-tags-button"
          >
            Manage Tags
          </Button>

          {/* Bulk Assignee Update */}
          <Menu shadow="md" width={250} position="bottom-start">
            <Menu.Target>
              <Button
                leftSection={<IconUserCheck size={16} />}
                rightSection={<IconChevronDown size={16} />}
                variant="default"
                size="md"
                loading={isLoadingMembers}
                disabled={isLoadingMembers}
                className="bulk-assignee-button"
              >
                Change Assignee
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <ScrollArea.Autosize
                type="always"
                scrollbarSize={8}
                offsetScrollbars
                mah={300}
              >
                {assigneeMenuItems.length > 0 ? (
                  assigneeMenuItems
                ) : (
                  <Menu.Item disabled>
                    <Text size="sm" c="dimmed">
                      No members available
                    </Text>
                  </Menu.Item>
                )}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* Archive/Unarchive Confirmation Modal */}
      <Modal
        opened={archiveModalOpened}
        onClose={closeArchiveModal}
        title={
          <Group gap="sm">
            <IconAlertTriangle
              size={20}
              color="var(--mantine-color-orange-6)"
            />
            <Text fw={600}>
              {archiveStatus === "all-archived" ? "Unarchive" : "Archive"}{" "}
              Clients
            </Text>
          </Group>
        }
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            You are about to{" "}
            {archiveStatus === "all-archived" ? "unarchive" : "archive"}{" "}
            <Text component="span" fw={600} c="dark">
              {selectedCount} client{selectedCount !== 1 ? "s" : ""}
            </Text>
            :
          </Text>

          {/* List of client names */}
          <ScrollArea.Autosize mah={200} type="auto">
            <List size="sm" spacing="xs">
              {selectedClientNames.map((name, index) => (
                <List.Item key={index}>
                  <Text size="sm" fw={500}>
                    {name}
                  </Text>
                </List.Item>
              ))}
            </List>
          </ScrollArea.Autosize>

          <Text size="sm" c="orange" fw={500}>
            {archiveStatus === "all-archived"
              ? "Unarchived clients will be restored to the active client list."
              : "Archived clients will be removed from the active client list."}
          </Text>

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="subtle"
              onClick={closeArchiveModal}
              disabled={isArchiving}
            >
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => confirmArchive(archiveStatus === "all-active")}
              loading={isArchiving}
              leftSection={<IconArchive size={16} />}
            >
              {archiveStatus === "all-archived" ? "Unarchive" : "Archive"}{" "}
              {selectedCount} Client{selectedCount !== 1 ? "s" : ""}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Manage Tags Modal */}
      <ManageClientTagsModal
        opened={tagsModalOpened}
        onClose={closeTagsModal}
        selectedClientIds={selectedClientIds}
        selectedClientNames={selectedClientNames}
        onUpdateComplete={onUpdateComplete}
      />
    </div>
  );
};

export default BulkActionsToolbar;
